import { addDays } from 'date-fns';
import {
  type AuthClient,
  AuthEvent,
  type AuthSession,
  type Credentials,
  type EventHandler,
  type EventSubscription,
  type Hub,
  type HubCapsule,
  type PromiseOfOptional,
  type SessionClient,
  type StorageClient,
} from '../interfaces';
import { CredentialsSchema } from '../interfaces/auth/AuthClient';
import { AuthSessionClient } from './AuthSessionClient';

export enum AuthTokenName {
  AccessToken = 'accessToken',
  IdentityToken = 'idToken',
  RefreshToken = 'refreshToken',
}

export abstract class BaseAuthClient<T> implements AuthClient<T> {
  protected session?: AuthSession<T>;
  constructor(
    protected readonly storage: StorageClient,
    private readonly prefix: string,
    protected readonly sessionClient: SessionClient<T>,
    protected readonly hub?: Hub,
    protected readonly autoRefresh = true,
  ) {}

  getStorageKey(name: string): string {
    return `${this.prefix}.${name}`;
  }

  async getTokenExpiryDate(type: AuthTokenName, token: string): Promise<Date> {
    if (type === AuthTokenName.RefreshToken) {
      // TODO: Find out the actual expiry date for refresh tokens
      return addDays(new Date(), 30);
    }

    const expiry = AuthSessionClient.getTokenExpiry(token);

    return new Date(expiry);
  }

  protected async storeCredentials(credentials: Credentials) {
    const keys = Object.keys(credentials) as AuthTokenName[];

    await Promise.all(
      keys.map(async (key) => {
        const storageKey = this.getStorageKey(key);

        const value = credentials[key];
        const date = await this.getTokenExpiryDate(key, value);
        const expireDate = new Date(date);
        return this.storage.setItem(storageKey, value, expireDate);
      }),
    );
  }

  protected async clearCredentials() {
    const keys = Object.values(AuthTokenName) as string[];

    await Promise.all(
      keys.map((k) => this.storage.removeItem(this.getStorageKey(k))),
    );
  }

  protected async initializeSession(credentials: Credentials) {
    const session = this.sessionClient.createSession(credentials);
    if (!session.isValid()) {
      return;
    }

    this.session = session;
    await this.storeCredentials(credentials);
  }

  async clearSession() {
    await this.clearCredentials();
    this.session = undefined;
  }

  public addEventListener(
    event: AuthEvent,
    handler: EventHandler,
  ): EventSubscription {
    const eventHandler = (data: HubCapsule) => {
      const isEvent = data.payload.event === event;
      if (isEvent) {
        handler();
      }
    };

    this.hub?.listen(this.prefix, eventHandler);

    return {
      remove: () => {
        this.hub?.remove(this.prefix, eventHandler);
      },
    };
  }

  private dispatchEvent(event: AuthEvent) {
    this.hub?.dispatch(this.prefix, { event });
  }

  private async revalidateSession() {
    const newCredentials = await this.refreshTokens().catch(() => undefined);

    if (!newCredentials) {
      await this.clearCredentials();
      this.session = undefined;
      this.dispatchEvent(AuthEvent.SignOut);
      return;
    }
    const newSession = this.sessionClient.createSession(newCredentials);

    if (newSession.isValid()) {
      this.session = newSession;
    }
  }

  async getSession(): PromiseOfOptional<AuthSession<T>> {
    if (this.session?.isValid()) {
      return this.session;
    }

    const currentCredentials = await this.getCredentials();
    if (!currentCredentials) {
      this.autoRefresh && (await this.revalidateSession());

      return this.session;
    }

    const session = this.sessionClient.createSession(currentCredentials);

    if (session.isValid()) {
      this.session = session;
      return session;
    }

    if (this.autoRefresh) {
      await this.revalidateSession();
    }

    return this.session;
  }

  async getCredentials(): PromiseOfOptional<Credentials> {
    const keys = Object.values(AuthTokenName) as string[];

    const values = await Promise.all(
      keys.map(async (key) => {
        const storageKey = this.getStorageKey(key);
        const value = await this.storage.getItem(storageKey);

        if (!value) {
          return {};
        }

        return { [key]: value };
      }),
    );

    const tokens = values.reduce((acc, val) => ({ ...acc, ...val }), {});

    const parsed = CredentialsSchema.safeParse(tokens);

    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }

  async signOut(): Promise<void> {
    await this.clearSession();
    this.dispatchEvent(AuthEvent.SignOut);
  }
  abstract getUser(): PromiseOfOptional<T>;
  abstract refreshTokens(): PromiseOfOptional<Credentials>;
}
